<template>
  <div class="dashboard">

    <div class="main-content">
      <navbar-dashboard name="Usuarios"></navbar-dashboard>

      <div class="ew-view">
        <page-header title="Usuarios y roles" subtitle="Administra quién accede a la plataforma y con qué permisos.">
          <button class="btn btn-primary" @click="showModal">
            <i class="bi bi-plus-lg mr-1"></i> Nuevo usuario
          </button>
        </page-header>

        <!-- Métricas -->
        <div class="ew-stats">
          <stat-card label="Usuarios totales" icon="bi bi-people" tone="navy" :loading="isLoading && !users.length"
            :value="totalCount | dash"></stat-card>
          <stat-card label="Activos" icon="bi bi-person-check" tone="success" :loading="isLoading && !users.length"
            :value="countActivos | dash"></stat-card>
          <stat-card label="Bloqueados" icon="bi bi-person-lock" tone="neutral" :loading="isLoading && !users.length"
            :value="countBloqueados | dash"></stat-card>
        </div>

        <!-- Modal Nuevo Usuario -->
        <b-modal ref="addUser" hide-footer :title="title">
          <form @submit.prevent="registerUser">
            <div class="form-group">
              <label>Nombre <sup>*</sup></label>
              <input type="text" class="form-control" v-model="firstName" required
                v-b-tooltip.hover.top="'Ingresa el nombre del usuario'">
            </div>
            <div class="form-group">
              <label>Apellidos <sup>*</sup></label>
              <input type="text" class="form-control" v-model="lastName" required
                v-b-tooltip.hover.top="'Ingresa los apellidos del usuario'">
            </div>
            <div class="form-group">
              <label>Teléfono <sup>*</sup></label>
              <input type="text" class="form-control" v-model="phone" required
                v-b-tooltip.hover.top="'Ingresa el número de teléfono de contacto'">
            </div>
            <div class="form-group">
              <label>Correo <sup>*</sup></label>
              <input type="email" class="form-control" v-model="email" required
                v-b-tooltip.hover.top="'Ingresa el correo electrónico para acceso'">
            </div>
            <div class="form-group">
              <label>Contraseña <sup>*</sup></label>
              <input type="password" class="form-control" v-model="password" required
                v-b-tooltip.hover.top="'Define una contraseña segura para el usuario'">
            </div>
            <div class="form-group" v-if="!hasRole(['helpdesk', 'sales'])">
              <label>Rol <sup>*</sup></label>
              <select class="form-control" v-model="rol" v-b-tooltip.hover.top="'Asigna un nivel de acceso al usuario'"
                required>
                <option disabled value="">Selecciona un rol</option>
                <option v-for="rol in rolesFiltrados" :key="rol._id" :value="rol._id">
                  {{ rol.nombre }}
                </option>
              </select>
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">Crear usuario</button>
            </div>
          </form>
        </b-modal>

        <!-- Modal Abonar --><b-modal ref="modalAbonar" hide-footer title="Abonar saldo">
          <form @submit.prevent="abonarPrepaid">
            <div class="form-group">
              <label>Cantidad a abonar <sup>*</sup></label>
              <input type="number" min="0.01" step="0.01" class="form-control" v-model="cantidadAbonar" required
                v-b-tooltip.hover.top="'Ingresa el monto a sumar al saldo del usuario'">
              <input type="hidden" v-model="userIdAbonar">
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideModalAbonar">Cancelar</button>
              <button type="submit" class="btn btn-primary">Abonar saldo</button>
            </div>
          </form>
        </b-modal>
        <!-- Modal Editar Usuario -->
        <b-modal ref="editUser" hide-footer :title="'Editar usuario'">
          <form @submit.prevent="updateUser">
            <div class="form-group">
              <label>Nombre <sup>*</sup></label>
              <input type="text" class="form-control" v-model="firstName" required
                v-b-tooltip.hover.top="'Modifica el nombre del usuario'">
            </div>
            <div class="form-group">
              <label>Apellidos <sup>*</sup></label>
              <input type="text" class="form-control" v-model="lastName" required
                v-b-tooltip.hover.top="'Modifica los apellidos del usuario'">
            </div>
            <div class="form-group">
              <label>Teléfono <sup>*</sup></label>
              <input type="text" class="form-control" v-model="phone" required
                v-b-tooltip.hover.top="'Actualiza el número de contacto'">
            </div>
            <div class="form-group">
              <label>Correo <sup>*</sup></label>
              <input type="email" class="form-control" v-model="email" required
                v-b-tooltip.hover.top="'Actualiza el correo electrónico del usuario'">
            </div>
            <div class="form-group">
              <label>Nueva contraseña</label>
              <input type="password" class="form-control" v-model="password"
                placeholder="Déjala vacía para conservar la actual">
            </div>
            <div class="form-group" v-if="!hasRole(['helpdesk', 'sales'])">
              <label>Rol</label>
              <select class="form-control" v-model="rol"
                v-b-tooltip.hover.top="'Selecciona un nuevo rol para el usuario'">
                <option disabled value="">Selecciona un rol</option>
                <option v-for="rol in rolesFiltrados" :key="rol._id" :value="rol._id">
                  {{ rol.nombre }}
                </option>
              </select>
            </div>
            <div class="text-right">
              <button type="button" class="btn btn-outline-secondary mr-2" @click="hideEditModal">Cancelar</button>
              <button type="submit" class="btn btn-primary">Guardar cambios</button>
            </div>
          </form>
        </b-modal>

        <!-- Filtros -->
        <div class="card ew-filters mb-3">
          <div class="form-row">
            <div class="col-12 col-md-6 form-group">
              <label for="user-search">Buscar</label>
              <div class="ew-search">
                <i class="bi bi-search"></i>
                <input id="user-search" type="search" class="form-control" v-model="searchFilter"
                  placeholder="Nombre, apellido o correo">
              </div>
            </div>
            <div class="col-6 col-md-3 form-group" v-if="!hasRole(['client'])">
              <label for="user-role">Rol</label>
              <select id="user-role" class="custom-select" v-model="rolFilter">
                <option value="">Todos</option>
                <option v-for="r in roles" :key="r._id" :value="r._id">{{ r.nombre }}</option>
              </select>
            </div>
            <div class="col-6 col-md-3 form-group">
              <label for="user-status">Estatus</label>
              <select id="user-status" class="custom-select" v-model="activeFilter">
                <option value="">Todos</option>
                <option value="true">Activos</option>
                <option value="false">Bloqueados</option>
              </select>
            </div>
          </div>
        </div>

        <div class="ew-table-scroll">
          <table class="table table-hover mb-0">
            <thead>
              <tr>
                <th>Usuario</th>
                <th>Teléfono</th>
                <th class="ew-num">Monedero</th>
                <th>Cliente</th>
                <th>Rol</th>
                <th>Estatus</th>
                <th class="text-right">Acciones</th>
              </tr>
            </thead>
            <tbody v-if="isLoading && !users.length">
              <tr v-for="n in 6" :key="'sk-' + n">
                <td v-for="c in 7" :key="'skc-' + c"><span class="ew-skeleton"></span></td>
              </tr>
            </tbody>
            <tbody v-else-if="users.length">
              <tr v-for="u in users" :key="u._id">
                <td>
                  <div class="d-flex align-items-center">
                    <span class="ew-avatar">{{ initials(u) }}</span>
                    <div class="ew-party ew-party--wide">
                      <span class="ew-party-name">{{ [u.firstName, u.lastName].filter(Boolean).join(' ') | dash }}</span>
                      <span class="ew-party-place">{{ u.email | dash }}</span>
                    </div>
                  </div>
                </td>
                <td class="text-nowrap">{{ u.phone ? u.phone.trim() : '' | dash }}</td>
                <td class="ew-num">{{ u.total | money }}</td>
                <td>{{ parentFirstName(u) | dash }}</td>
                <td class="text-nowrap">{{ getRoleText(u.rol) }}</td>
                <td>
                  <span class="badge" :class="u.active ? 'badge-success' : 'badge-secondary'">
                    {{ u.active ? 'Activo' : 'Bloqueado' }}
                  </span>
                </td>
                <td class="text-right text-nowrap">
                  <button class="btn btn-sm btn-outline-secondary ew-icon-btn mr-1" @click="showEditModal(u)"
                    aria-label="Editar usuario" v-b-tooltip.hover.top="'Editar'">
                    <i class="bi bi-pencil"></i>
                  </button>
                  <b-dropdown v-if="!hasRole(['client']) || u._id !== user_id" right no-caret size="sm"
                    variant="outline-secondary" class="ew-row-menu" toggle-class="ew-icon-btn"
                    :popper-opts="{ positionFixed: true }">
                    <template #button-content>
                      <i class="bi bi-three-dots"></i><span class="sr-only">Más acciones</span>
                    </template>
                    <b-dropdown-item v-if="!hasRole(['client'])" @click="showModalAbonar(u._id)">
                      <i class="bi bi-wallet2 mr-2"></i> Abonar saldo
                    </b-dropdown-item>
                    <template v-if="u._id !== user_id">
                      <b-dropdown-item v-if="!u.active" @click="activarUsuario(u._id)">
                        <i class="bi bi-person-check mr-2"></i> Activar acceso
                      </b-dropdown-item>
                      <b-dropdown-item v-else link-class="text-danger" @click="confirmDeactivate(u)">
                        <i class="bi bi-person-lock mr-2"></i> Bloquear acceso
                      </b-dropdown-item>
                    </template>
                  </b-dropdown>
                </td>
              </tr>
            </tbody>
            <tbody v-else>
              <tr>
                <td colspan="7" class="p-0">
                  <empty-state v-if="searchFilter || rolFilter || activeFilter" icon="bi bi-search"
                    title="Sin coincidencias" text="No encontramos usuarios con esos filtros."></empty-state>
                  <empty-state v-else icon="bi bi-people" title="Aún no hay usuarios"
                    text="Invita a tu equipo para que pueda cotizar y generar guías.">
                    <button class="btn btn-primary" @click="showModal">
                      <i class="bi bi-plus-lg mr-1"></i> Nuevo usuario
                    </button>
                  </empty-state>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <table-pagination :page="currentPage" :total-pages="totalPages" @change="changePage" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.ew-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  margin-right: 10px;
  border-radius: 50%;
  background: #e8ebf5;
  color: #031d7d;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.ew-party--wide {
  max-width: 260px;
}
</style>

<script>
import Navbar from './Header/Navbar'
import { mapGetters, mapMutations } from 'vuex'
import axios from 'axios'
import moment from 'moment'
import commons from '../commons'
import Vue from 'vue'
import { roleMixin } from '@/mixins/roleMixin';

export default {
  name: 'Users',
  mixins: [roleMixin],
  data() {
    return {
      title: 'Nuevo usuario',
      cantidadAbonar: '',
      userIdAbonar: '',
      firstName: '',
      lastName: '',
      phone: '',
      email: '',
      password: '',
      user_id: '',
      users: [],
      results: [],
      scroll: 0,
      isLoading: false,
      arrowCounter: -1,
      searchFilter: '',
      rolFilter: '',
      activeFilter: '',
      filterTimeout: null, // Para el debouncing de la búsqueda
      // Variables de Paginación
      totalPages: 1, // Total de páginas disponibles (viene de 'pages' en la respuesta)
      currentPage: 1, // Página actual (será enviada en la petición GET)
      perPage: 10,    // Límite de ítems por página (será enviada en la petición GET)
      totalCount: 0,  // Conteo total de ítems (viene de 'count' en la respuesta)
      countActivos: 0,  // Conteo total de ítems (viene de 'count' en la respuesta)
      countBloqueados: 0,  // Conteo total de ítems (viene de 'count' en la respuesta)
      rol: '',
      roles: [
        { _id: "superadmin", nombre: "Superadmin [Interno SackTech]" },
        { _id: "helpdesk", nombre: "Servicio al Cliente [Interno SackTech]" },
        { _id: "sales", nombre: "Ventas [Interno SackTech]" },
        { _id: "client", nombre: "Administrador" },
        { _id: "user", nombre: "Operativo" },
        { _id: "admin", nombre: "Compras/Administración" },
        { _id: "admin_add", nombre: "Administrador Adicional" },]
    }
  },
  created() {
    this.user_id = localStorage.getItem('ew-user-id')
    this.getUsers()
    this.setItemActive('users')
  },
  methods: {
    ...mapMutations(['setItemActive']),
    parentFirstName(u) {
      return u && u.parentClient ? `${u.parentClient.firstName} ${u.parentClient.lastName}` : '';
    },
    formatCurrency(value) {
      return Number(value).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
    },
    initials(u) {
      const a = (u.firstName || '').trim().charAt(0);
      const b = (u.lastName || '').trim().charAt(0);
      return ((a + b) || (u.email || '?').charAt(0)).toUpperCase();
    },
    confirmDeactivate(u) {
      const name = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
      this.$bvModal.msgBoxConfirm(`${name} ya no podrá iniciar sesión hasta que reactives su acceso.`, {
        title: 'Bloquear acceso',
        okTitle: 'Bloquear',
        okVariant: 'danger',
        cancelTitle: 'Cancelar',
        cancelVariant: 'outline-secondary',
        centered: true
      }).then(ok => { if (ok) this.desactivarUsuario(u._id) })
    },
    getRoleText(rol) {
      const role = this.roles.find(r => r._id === rol);
      return role ? role.nombre : "-";
    },
    showEditModal(user) {
      this.user_idEdit = user._id
      this.firstName = user.firstName
      this.lastName = user.lastName
      this.phone = user.phone
      this.email = user.email
      this.rol = user.rol ? user.rol : ''
      this.password = ''
      this.$refs.editUser.show()
    },

    hideEditModal() {
      this.$refs.editUser.hide()
    },
    updateUser() {
      const payload = {
        firstName: this.firstName,
        lastName: this.lastName,
        phone: this.phone,
        email: this.email,
      }

      if (this.password) {
        payload.password = this.password
      }
      if (this.rol) {
        payload.rol = this.rol;
      }

      axios
        .put(`${commons.apiURL}/api/v1/users/${this.user_idEdit}`, payload)
        .then(response => {
          if (response.status == 200) {
            this.hideEditModal()
            this.getUsers()
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.getUsers();
      }
    },
    formatDateTime(value) {
      if (value) {
        return moment(String(value)).format('DD/MM/YYYY HH:mm:ss')
      }
    },
    async getUsers() {
      let userRol = localStorage.getItem('ew-user-role');

      const userEndpoint = userRol == 'client'
        ? `${commons.apiURL}/api/v1/users/client/${this.user_id}`
        : `${commons.apiURL}/api/v1/users`;

      this.isLoading = true;
      try {
        const usersResponse = await axios.get(userEndpoint, {
          params: {
            page: this.currentPage,
            limit: this.perPage,
            // Filtros Específicos (se envían si tienen valor)
            filter: this.searchFilter,
            rol: this.rolFilter,
            active: this.activeFilter,
          }
        });
        const userDocs = usersResponse.data.docs || usersResponse.data.docs;
        const { count, page, pages, countActivos, countBloqueados } = usersResponse.data;

        this.totalPages = pages;
        this.currentPage = page;
        this.totalCount = count;
        this.countActivos = countActivos;
        this.countBloqueados = countBloqueados;
        const prepaidPromises = userDocs.map(async (item) => {
          try {
            const prepaidResponse = await axios.get(`${commons.apiURL}/api/v1/prepaid?user=${item._id}`);
            const prepaids = prepaidResponse.data;

            let total = 0;
            for (let prepaidItem of prepaids) {
              if (prepaidItem.status) {
                total += parseFloat(prepaidItem.amount || 0);
              }
            }
            return {
              ...item, // Usa el operador spread para copiar todas las propiedades de 'item'
              total: total,
            };

          } catch (error) {
            console.error(`Error al obtener prepagos para el usuario ${item._id}:`, error);

            return { ...item, total: 0 };
          }
        });
        this.users = await Promise.all(prepaidPromises);

      } catch (error) {
        console.error("Error al obtener la lista de usuarios principal:", error);
      } finally {
        this.isLoading = false;
      }
    },
    abonarPrepaid() {
      axios
        .post(`${commons.apiURL}/api/v1/prepaid/createPrepaid`, {
          cantidadAbonar: this.cantidadAbonar,
          userId: this.userIdAbonar,
        })
        .then(response => {
          if (response.status == 200) {
            this.hideModalAbonar()
            this.getUsers()
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    activarUsuario(id) {
      axios
        .put(`${commons.apiURL}/api/v1/users/${id}`,
          {
            active: true
          })
        .then(response => {
          if (response.status == 200) {
            this.getUsers()
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    desactivarUsuario(id) {
      axios
        .put(`${commons.apiURL}/api/v1/users/${id}`,
          {
            active: false
          })
        .then(response => {
          if (response.status == 200) {
            this.getUsers()
          }
        })
        .catch(error => {
          console.error(error)
        })
    },
    async registerUser() {
      const userRol = localStorage.getItem('ew-user-role');
      const userId = localStorage.getItem('ew-user-id');

      if (userRol == 'sales') {
        this.rol = 'client';
      }
      const userData = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        phone: this.phone,
        password: this.password,
        rol: this.rol ? this.rol : 'user',
      };

      try {
        const response = await axios.post(
          `${commons.apiURL}/api/v1/users/createFromDashboard`,
          userData
        );

        if (response.status === 200) {
          if (userRol == 'client') {
            await this.assignUser(userId, response.data.id);
          }

          this.hideModal();
          this.getUsers();
        }
      } catch (error) {
        console.error("Error al registrar usuario:", error);
      }
    },
    async assignUser(selectedClientId, selectedUserId) {
      if (!selectedClientId || !selectedUserId) return;
      try {
        await axios.patch(
          `${commons.apiURL}/api/v1/client/${selectedClientId}/assign-user/${selectedUserId}`
        );
      } catch (err) {
        console.error("Error al asignar usuario:", err);
      }
    },
    showModal() {
      this.user_idEdit = ''
      this.firstName = ''
      this.lastName = ''
      this.phone = ''
      this.email = ''
      this.password = ''
      this.$refs.addUser.show()
    },
    hideModal() {
      this.$refs.addUser.hide()
    },
    hideModalAbonar() {
      this.$refs.modalAbonar.hide()
    },
    showModalAbonar(user_id) {
      this.cantidadAbonar = '';
      this.userIdAbonar = user_id;
      this.$refs.modalAbonar.show()
    },
    runSearch() {
      // Vuelve a la página 1 cada vez que se cambia cualquier filtro
      this.currentPage = 1;

      // Debouncing: Espera 300ms después de que el usuario deja de teclear
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }
      this.filterTimeout = setTimeout(() => {
        this.getUsers();
      }, 300);
    },
  },
  watch: {
    searchFilter() {
      this.runSearch();
    },
    rolFilter() {
      this.runSearch();
    },
    activeFilter() {
      this.runSearch();
    }
  },
  computed: {
    ...mapGetters(['itemActive']),
    maxButtons() {
      return 5; // Por ejemplo, mostrar la página actual + 2 a cada lado
    },
    paginationRange() {
      const total = this.totalPages;
      const current = this.currentPage;
      const max = this.maxButtons;

      if (total <= max) {
        // Caso 1: Si el total de páginas es pequeño, mostrar todas.
        return Array.from({ length: total }, (_, i) => i + 1);
      }

      // Caso 2: Total de páginas grande, necesitamos limitar el rango.

      let start = current - Math.floor(max / 2);
      let end = current + Math.floor(max / 2);

      // Asegurarse de que el inicio no sea menor que 1
      if (start < 1) {
        start = 1;
        end = max;
      }

      // Asegurarse de que el final no exceda el total
      if (end > total) {
        end = total;
        start = total - max + 1;
        // Asegurarse de que start no sea menor que 1 después del ajuste
        if (start < 1) start = 1;
      }

      // Crear el array de números de página
      const length = end - start + 1;
      return Array.from({ length }, (_, i) => start + i);
    },
    rolesFiltrados() {
      let userRol = localStorage.getItem('ew-user-role');
      const rolesQueFiltran = ['client'];
      const rolesAExcluir = ["superadmin", "helpdesk", "sales", "client"];
      if (rolesQueFiltran.includes(userRol)) {
        return this.roles.filter(rol => {
          return !rolesAExcluir.includes(rol._id);
        });

      } else {
        return this.roles;
      }
    },
  },
  components: {
    'navbar-dashboard': Navbar
  },
}
</script>



// WEBPACK FOOTER //
// src/components/Users.vue